import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef } from "react";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LanguageProvider } from "@/lib/i18n";
import { GameFeelProvider } from "@/context/GameFeel";
import api from "@/lib/api";

import AuthPage from "@/pages/AuthPage";
import Layout from "@/components/Layout";
import Casino from "@/pages/Casino";
import Tournaments from "@/pages/Tournaments";
import Duels from "@/pages/Duels";
import BattlePass from "@/pages/BattlePass";
import VipLounge from "@/pages/VipLounge";
import Wallet from "@/pages/Wallet";
import Leaderboard from "@/pages/Leaderboard";
const Admin = lazy(() => import("@/pages/Admin"));
import Responsible from "@/pages/Responsible";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import Invite from "@/pages/Invite";
const Partners = lazy(() => import("@/pages/Partners"));
import ReferralCapture from "@/components/ReferralCapture";
import { PaymentSuccess, PaymentCancel } from "@/pages/PaymentResult";
import MarketingLayout from "@/components/MarketingLayout";
import { HowToPlay, Guides, Blog, BlogPost, Fairness } from "@/pages/marketing/Content";
import { About, Contact, NotFound } from "@/pages/marketing/Company";
import Games from "@/pages/marketing/Games";
import SmsOptIn from "@/pages/marketing/SmsOptIn";
import SlotGame from "@/pages/games/SlotGame";
import Mahjong from "@/pages/games/Mahjong";
import LiveCrash from "@/pages/games/LiveCrash";
import PlinkoGame from "@/pages/games/PlinkoGame";
import WheelGame from "@/pages/games/WheelGame";
import MinesGame from "@/pages/games/MinesGame";
import CrashMinesGame from "@/pages/games/CrashMinesGame";
import WheelSlotsGame from "@/pages/games/WheelSlotsGame";
import BlackjackGame from "@/pages/games/BlackjackGame";
import RouletteGame from "@/pages/games/RouletteGame";
import BaccaratGame from "@/pages/games/BaccaratGame";

function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const hash = window.location.hash;
    const sid = new URLSearchParams(hash.replace("#", "")).get("session_id");
    (async () => {
      try {
        const { data } = await api.post("/auth/session", { session_id: sid });
        setUser(data);
        window.history.replaceState(null, "", "/casino");
        navigate("/casino", { replace: true });
      } catch {
        navigate("/", { replace: true });
      }
    })();
  }, [navigate, setUser]);
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#07080D] text-white">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="font-display text-lg">Entering the Arena…</p>
      </div>
    </div>
  );
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#07080D] text-white">
        <div className="w-10 h-10 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  if (!user) return <Navigate to="/" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <>
    <ReferralCapture />
    <Suspense fallback={<div className="min-h-screen bg-[#0b0b14]" />}>
    <Routes>
      <Route path="/" element={<AuthPage />} />
      <Route path="/partners" element={<Partners />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route element={<MarketingLayout />}>
        <Route path="/how-to-play" element={<HowToPlay />} />
        <Route path="/fairness" element={<Fairness />} />
        <Route path="/guides" element={<Guides />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/games" element={<Games />} />
        <Route path="/sms-opt-in" element={<SmsOptIn />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="*" element={<NotFound />} />
      </Route>
      <Route
        element={
          <Protected>
            <Layout />
          </Protected>
        }
      >
        <Route path="/casino" element={<Casino />} />
        <Route path="/casino/slot" element={<SlotGame />} />
        <Route path="/casino/mahjong" element={<Mahjong />} />
        <Route path="/casino/crash" element={<LiveCrash />} />
        <Route path="/casino/plinko" element={<PlinkoGame />} />
        <Route path="/casino/wheel" element={<WheelGame />} />
        <Route path="/casino/mines" element={<MinesGame />} />
        <Route path="/casino/crashmines" element={<CrashMinesGame />} />
        <Route path="/casino/wheelslots" element={<WheelSlotsGame />} />
        <Route path="/casino/blackjack" element={<BlackjackGame />} />
        <Route path="/casino/roulette" element={<RouletteGame />} />
        <Route path="/casino/baccarat" element={<BaccaratGame />} />
        <Route path="/tournaments" element={<Tournaments />} />
        <Route path="/duels" element={<Duels />} />
        <Route path="/battlepass" element={<BattlePass />} />
        <Route path="/battle-pass" element={<Navigate to="/battlepass" replace />} />
        <Route path="/vip" element={<VipLounge />} />
        <Route path="/invite" element={<Invite />} />
        <Route path="/wallet" element={<Wallet />} />
        <Route path="/store" element={<Wallet initialTab="coins" />} />
        <Route path="/shop" element={<Wallet initialTab="coins" />} />
        <Route path="/leaderboard" element={<Leaderboard />} />
        <Route path="/responsible-gaming" element={<Responsible />} />
        <Route path="/responsible-play" element={<Navigate to="/responsible-gaming" replace />} />
        <Route path="/responsible" element={<Navigate to="/responsible-gaming" replace />} />
        <Route path="/admin" element={<Admin />} />
      </Route>
    </Routes>
    </Suspense>
    </>
  );
}

export default function App() {
  return (
    <div className="App grain">
      <LanguageProvider>
        <AuthProvider>
          <GameFeelProvider>
          <BrowserRouter>
            <Toaster position="top-center" theme="dark" richColors closeButton />
            <AppRouter />
          </BrowserRouter>
          </GameFeelProvider>
        </AuthProvider>
      </LanguageProvider>
    </div>
  );
}
